import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion, useScroll, useTransform, useMotionValueEvent, useReducedMotion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { SERVICES } from "../data/services";
import { IMG, imgProps } from "../data/assets";

const EASE = [0.22, 1, 0.36, 1];
const COUNT = SERVICES.length;

// SCENE 03: immersive service explorer — one service dominates the screen,
// permanent index on the left, environment image shifts per service.
export function ServiceExplorer() {
  const ref = useRef(null);
  const [active, setActive] = useState(0);
  const reduce = useReducedMotion();
  const { scrollYProgress: p } = useScroll({ target: ref, offset: ["start start", "end end"] });
  const seg = useTransform(p, [0, 0.999], [0, COUNT - 1 + 0.999]);

  useMotionValueEvent(seg, "change", (v) => {
    const i = Math.min(COUNT - 1, Math.max(0, Math.round(v)));
    setActive((cur) => (cur === i ? cur : i));
  });

  const jump = (i) => {
    const el = ref.current;
    if (!el) return;
    const top = el.getBoundingClientRect().top + window.scrollY;
    const s = (el.offsetHeight - window.innerHeight) / COUNT;
    window.scrollTo({ top: top + s * i + 1, behavior: reduce ? "auto" : "smooth" });
  };

  // Static stacked list — reduced-motion + mobile (no pinned scroll on touch).
  const staticList = (
    <section className="noise-dark py-24 text-zinc-100">
      <div className="container-x space-y-16">
        {SERVICES.map((s) => (
          <div key={s.id} className="grid gap-6 border-b border-zinc-800 pb-12 md:grid-cols-2">
            <div>
              <p className="tech-no-dark">{s.num}</p>
              <h3 className="h-display mt-4 text-3xl text-zinc-50">{s.name}</h3>
              <p className="mt-4 text-sm text-zinc-400">{s.tagline}</p>
              <Link to={`/services/${s.slug}`} data-testid={`explorer-static-link-${s.id}`} className="link-tech mt-6 text-zinc-200">Explore service →</Link>
            </div>
            <img src={IMG[s.image].src} alt={IMG[s.image].alt} {...imgProps(IMG[s.image].src)} loading="lazy" decoding="async" className="aspect-[16/9] w-full border border-zinc-800 object-cover" />
          </div>
        ))}
      </div>
    </section>
  );

  const current = SERVICES[active];

  // Both variants render — CSS decides visibility (no hydration layout swap).
  return (
    <>
      <div className="md:hidden md:motion-reduce:block">{staticList}</div>
      <div className="hidden md:block md:motion-reduce:hidden">
        <section ref={ref} className="relative h-[145vh]" aria-label="Five specialist services">
      <div className="noise-dark sticky top-0 h-screen overflow-hidden text-zinc-100">
        <div className="absolute inset-0 grid-lines-dark" aria-hidden="true" />

        {/* environment imagery */}
        {SERVICES.map((s, i) => (
          <motion.div key={s.id} className="absolute inset-0"
            initial={false}
            animate={{ opacity: active === i ? 1 : 0 }}
            transition={{ duration: 0.5, ease: "easeInOut" }}>
            <motion.img src={IMG[s.explorerImage || s.image].src} alt="" aria-hidden="true" loading={i === 0 ? "eager" : "lazy"}
              animate={{ scale: active === i ? 1 : 1.06 }}
              transition={{ duration: 1.2, ease: EASE }}
              style={{ objectPosition: IMG[s.explorerImage || s.image].pos }}
              className="h-full w-full object-cover will-change-transform" />
          </motion.div>
        ))}
        <div className="absolute inset-0 bg-charcoal/60" aria-hidden="true" />

        <div className="container-x relative flex h-full">
          {/* permanent index — direct selection by scroll, touch or keyboard */}
          <nav aria-label="Choose a service directly" className="flex flex-col justify-center gap-1 border-r border-zinc-800/80 pr-6 md:pr-10">
            {SERVICES.map((s, i) => (
              <button
                key={s.id}
                type="button"
                onClick={() => jump(i)}
                data-testid={`explorer-index-${s.id}`}
                aria-label={`Go to ${s.name}`}
                aria-current={active === i ? "true" : undefined}
                className={`flex min-h-[44px] items-baseline gap-3 py-2 text-left transition-all duration-200 ${
                  active === i ? "translate-x-2 text-dcc" : "text-zinc-400 hover:text-zinc-300"
                }`}
              >
                <span className="font-mono text-sm tracking-[0.25em] md:text-base">{s.num}</span>
                <span className={`hidden font-mono text-xs uppercase tracking-[0.18em] md:inline ${active === i ? "text-zinc-300" : "text-zinc-400"}`}>
                  {s.short}
                </span>
              </button>
            ))}
          </nav>

          {/* dominant service content */}
          <div className="relative flex flex-1 items-center pl-6 md:pl-14">
            <motion.div style={{ scaleY: p }} className="absolute left-0 top-[15%] h-[70%] w-px origin-top bg-dcc" aria-hidden="true" />
            <AnimatePresence mode="wait">
              <motion.div
                key={current.id}
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.35, ease: EASE }}
              >
                <p className="font-mono text-xs uppercase tracking-[0.3em] text-dcc">
                  Service {current.num} / {String(COUNT).padStart(2, "0")}
                </p>
                <h3 className="h-display mt-5 max-w-4xl text-4xl leading-[0.95] text-zinc-50 sm:text-5xl lg:text-7xl">
                  {current.name.replace("Commercial ", "")}
                </h3>
                <p className="mt-6 max-w-md text-sm leading-relaxed text-zinc-300 md:text-base">{current.tagline}</p>
                <Link to={`/services/${current.slug}`} data-testid={`explorer-link-${current.id}`} data-cursor="EXPLORE"
                  className="link-tech mt-8 text-zinc-100">
                  Explore service <ArrowRight size={14} aria-hidden="true" />
                </Link>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>

        <p className="absolute left-6 top-24 font-mono text-xs uppercase tracking-[0.3em] text-zinc-400 md:left-12 lg:left-20">
          02 / Five specialist cleaning systems
        </p>
      </div>
        </section>
      </div>
    </>
  );
}
